import * as satellite from 'satellite.js';
import { SatelliteRecord, GeodeticPosition, ECIPosition } from '../types/satellite';

export class OrbitalMechanics {
  
  /**
   * Propagate satellite to geodetic coordinates at given time
   */
  static propagateToGeodetic(satrec: any, date: Date): GeodeticPosition | null {
    try {
      const propagation = satellite.propagate(satrec, date);
      
      if (!propagation.position || typeof propagation.position === 'boolean') {
        return null;
      }
      
      const gmst = satellite.gstime(date);
      const geodetic = satellite.eciToGeodetic(propagation.position, gmst);
      
      const lat = satellite.degreesLat(geodetic.latitude);
      const lon = satellite.degreesLong(geodetic.longitude);
      
      // Handle altitude units - satellite.js returns km for geodetic height
      // but some versions may vary, so we add a guard
      const altKm = Math.abs(geodetic.height) > 50000 
        ? geodetic.height / 1000  // Convert from meters if unreasonably large
        : geodetic.height;        // Already in km
      
      return { lat, lon, altKm };
      
    } catch (error) {
      console.warn('Failed to propagate satellite to geodetic:', error);
      return null;
    }
  }
  
  /**
   * Get ECI position and velocity at given time
   */
  static getECIPosition(satrec: any, date: Date): ECIPosition | null {
    try {
      const { position, velocity } = satellite.propagate(satrec, date);
      
      if (!position || typeof position === 'boolean') {
        return null;
      }
      
      return {
        x: position.x,
        y: position.y,
        z: position.z,
        vx: typeof velocity === 'boolean' ? 0 : velocity?.x ?? 0,
        vy: typeof velocity === 'boolean' ? 0 : velocity?.y ?? 0,
        vz: typeof velocity === 'boolean' ? 0 : velocity?.z ?? 0,
      };
      
    } catch (error) {
      console.warn('Failed to get ECI position:', error);
      return null;
    }
  }
  
  /**
   * Calculate distance between two ECI positions in kilometers
   */
  static calculateDistance(pos1: ECIPosition, pos2: ECIPosition): number {
    const dx = pos1.x - pos2.x;
    const dy = pos1.y - pos2.y;
    const dz = pos1.z - pos2.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }
  
  /**
   * Generate ground track for a satellite over time
   */
  static generateGroundTrack(
    satellite: SatelliteRecord,
    startTime: Date,
    steps: number,
    stepSeconds: number
  ): [number, number][] {
    const track: [number, number][] = [];
    
    for (let i = 0; i <= steps; i++) {
      const time = new Date(startTime.getTime() + i * stepSeconds * 1000);
      const position = this.propagateToGeodetic(satellite.satrec, time);
      
      if (position) {
        track.push([position.lat, position.lon]);
      }
    }
    
    return track;
  }
  
  /**
   * Calculate orbital period in minutes from satrec
   */
  static getOrbitalPeriod(satrec: any): number | null {
    try {
      // Mean motion is in revolutions per day
      const meanMotion = satrec.no; // radians per minute
      if (meanMotion > 0) {
        return (2 * Math.PI) / meanMotion; // minutes per revolution
      }
      return null;
    } catch {
      return null;
    }
  }
  
  /**
   * Get satellite altitude at current position
   */
  static getCurrentAltitude(satellite: SatelliteRecord, date: Date): number | null {
    const position = this.propagateToGeodetic(satellite.satrec, date);
    return position?.altKm ?? null;
  }
  
  /**
   * Check if satellite is in sunlight (basic calculation)
   * Returns true if satellite is illuminated by the Sun
   */
  static isInSunlight(satellite: SatelliteRecord, date: Date): boolean {
    try {
      const position = this.getECIPosition(satellite.satrec, date);
      if (!position) return false;
      
      // Earth radius in km
      const earthRadius = 6371;
      const satelliteDistance = Math.sqrt(position.x ** 2 + position.y ** 2 + position.z ** 2);
      
      // Simple shadow calculation - if satellite is below Earth horizon relative to Sun
      // This is a simplified model - proper calculation would need Sun position
      return satelliteDistance > earthRadius * 1.1; // Rough approximation
      
    } catch {
      return true; // Default to illuminated if calculation fails
    }
  }
}